import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import {
  ButtonComponent,
  CardComponent,
  ChipsComponent,
  FormInputComponent,
  IconComponent,
  IInputEvent,
  IInputEventType,
  IInputParam,
  ToastComponent,
} from '@sarasanalytics-com/design-system';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import {
  phoneNumberValidator,
  workEmailValidation,
} from 'src/app/validators/validators';
import {
  IMessageView,
  MessageViewComponent,
  MessageViewEvent,
} from 'src/app/shared/components/message-view/message-view.component';
import {
  SignupEmailVerificationFailure,
  SignupFailure,
  SignupSuccess,
} from 'src/app/static/message-view';
import {
  defaultSizeOptions,
  formLinks,
  inputField,
  productSignupProps,
  revenueSizeProps,
  signupCompanyProps,
  signupEmailProps,
  signupFirstNameProps,
  signupLastNameProps,
} from 'src/app/static/form';
import { SignupService } from '../../../services/signup/signup.service';
import { StoreService } from 'src/app/services/store/store.service';
import { IToast } from 'src/app/interfaces/toast-interface';
import { IAppParams, IUserInfo, Products, ProductTitle} from 'src/app/interfaces/state-interface';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { ActivatedRoute, ParamMap, RouterModule, Router } from '@angular/router';
import { switchMap, tap } from 'rxjs';
import { WebflowService } from 'src/app/services/signup/webflow.service';
import { LoginSignupPage } from 'src/app/interfaces/webflow-details.interface';
import { AppService } from 'src/app/services/app.service';
import { TrackingService } from 'src/app/services/tracking/tracking.service';
import { NS_Enums } from 'src/app/static/namespace';
import { PUBLIC_LINKS } from 'src/app/static/api.constant';
import { SupportService } from 'src/app/services/support/support.service';
import { ProductDetailService } from 'src/app/services/product/productdetails.service';
import { defaultProductOptions } from 'src/app/static/form';

@Component({
  selector: 'app-signup',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormlyModule,
    FormsModule,
    IconComponent,
    CardComponent,
    FormInputComponent,
    ButtonComponent,
    ChipsComponent,
    MessageViewComponent,
    ToastComponent,
    RouterModule
  ],
  providers: [SupportService],
  templateUrl: './signup.component.html',
  styleUrl: './signup.component.scss',
})
export class SignupComponent {
  details: any;
  LoginSignupPage: LoginSignupPage = { title: '', subtitle: '', cards: [], companies: [] };

  model: any = {};
  fields: FormlyFieldConfig[] = [];
  isFormFieldsReady: boolean = false;

  form: FormGroup;

  messageView: IMessageView | null; //=SignupSuccess('emma.taylor@brandxyz.com');
  showToast: boolean = false;
  toast: IToast | null;
  userInfo: IUserInfo;
  private afAuth: AngularFireAuth = inject(AngularFireAuth);
  private route: ActivatedRoute = inject(ActivatedRoute);
  private appService: AppService = inject(AppService);
  private signupService: SignupService = inject(SignupService);
  private store: StoreService = inject(StoreService);
  private webflowService: WebflowService = inject(WebflowService);
  private destroyRef: DestroyRef = inject(DestroyRef);
  private trackingService: TrackingService = inject(TrackingService);
  private productService: ProductDetailService = inject(ProductDetailService);
  isLoading: boolean = false;
  webflowContentReady = false;
  private router: Router = inject(Router);

  /* Page information */
  privacyPolicy: string = '';
  termsOfUse: string = '';

  product: Products = Products.SARAS;
  productTitle: string = '';
  loginLink: string = '';
  product_logo: string = '';

  sarasLink: string = '';

  constructor(private supportService:SupportService) {}
  ngOnInit() {
    this.privacyPolicy = formLinks.privacyPolicy;
    this.termsOfUse = formLinks.termsOfUse;
    this.route.queryParamMap.pipe(
      takeUntilDestroyed(this.destroyRef),
      tap(qp => {
        this.product = (
          qp.get('productName') ||
          qp.get('productType') ||
          Products.SARAS
        ).toUpperCase() as Products;
        if (this.product === Products.IQ) {
          this.product = Products.SARAS;
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {
              productType: this.product.toLowerCase(),
            },
            queryParamsHandling: 'merge',
            replaceUrl: true,
          });
        }
        this.productTitle = ProductTitle[this.product];
        this.product_logo = this.productService.getProductLogo(this.product);
        this.sarasLink = PUBLIC_LINKS[this.product] || PUBLIC_LINKS.SARAS;
        this.loginLink = formLinks.login(this.product.toLowerCase());
        this.prefillFromQueryParams(qp);
        this.webflowContentReady = false;
        this.generateForm();
      }),
      switchMap(() => this.webflowService.getSignupPageByProduct$(this.product)),
    ).subscribe((signupPage) => {
      this.LoginSignupPage.cards = signupPage.linkedCards;
      this.LoginSignupPage.companies = signupPage.linkedClients;
      this.LoginSignupPage.title = signupPage.pageTitle;
      this.LoginSignupPage.subtitle = signupPage.pageSubtitle;
      this.webflowContentReady = true;
    });
  }

  /**
   * Seeds the form from the Start Free Trial redirect so users don't re-enter details they
   * already gave. Missing params leave their fields empty, so direct visits behave as before.
   */
  private prefillFromQueryParams(qp: ParamMap) {
    this.model['email'] = this.appService.sanitizeEmailFromParams(qp.get('email') || '');
    this.model['company'] = (qp.get('company') || '').trim();
    this.model['annualRevenue'] = this.findRevenueOption(qp.get('annualRevenue'));
  }

  /**
   * Resolves a revenue value from the trial form to the matching tier option, if any.
   * Whitespace and case are ignored so `$10M - $50M` and `$10m-$50m` still match.
   */
  private findRevenueOption(value: string | null) {
    const normalized = this.normalizeRevenueText(value);
    if (!normalized) {
      return undefined;
    }
    return defaultSizeOptions.find(
      (option) =>
        this.normalizeRevenueText(option.value) === normalized ||
        this.normalizeRevenueText(option.name) === normalized
    );
  }

  /** Strips all whitespace and lowercases text so revenue labels compare loosely. */
  private normalizeRevenueText(text: string | null | undefined): string {
    return (text || '').replace(/\s+/g, '').toLowerCase();
  }

  generateForm() {
    this.form = new FormGroup({});
    
    const productStr = this.product.toString().toUpperCase();
    
    if (productStr === 'IQ') {
      this.model['productSigningUpFor'] = defaultProductOptions.find(p => p.value === 'Saras IQ');
    } else if (productStr === 'DATON') {
      this.model['productSigningUpFor'] = defaultProductOptions.find(p => p.value === 'Saras Daton');
    } else if (productStr === 'PULSE' || productStr === 'INSIGHTS') {
      this.model['productSigningUpFor'] = defaultProductOptions.find(p => p.value === 'Saras IQ');
    }

      this.fields = [
        {
          key: 'firstName',
          type: 'sa-input',
          className: 'flex-6 md-flex-12 block',
          props: signupFirstNameProps(this.model['firstName'] || ''),
        },
        {
          key: 'lastName',
          type: 'sa-input',
          className: 'flex-6 md-flex-12 block',
          props: signupLastNameProps(this.model['lastName'] || ''),
        },
        {
          key: 'email',
          type: 'sa-input',
          className: 'flex-12',
          props: signupEmailProps(this.model['email'] || ''),
        },
        {
          key: 'company',
          type: 'sa-input',
          className: 'flex-6 md-flex-12 block',
          props: signupCompanyProps(this.model['company'] || ''),
        },
        {
          key: 'annualRevenue',
          type: 'sa-select',
          className: 'flex-6 md-flex-12 block',
          props: revenueSizeProps(),
          validation: {
            messages: {
              required: 'Annual Revenue is required.',
            },
          },
        },
        {
          key: 'productSigningUpFor',
          type: 'sa-select',
          className: 'flex-12 block',
          props: productSignupProps(),
          validation: {
            messages: {
              required: 'Product signing up for is required.',
            },
          },
        },
      ];
      this.isFormFieldsReady = true;
      setTimeout(() => {
        this.form.updateValueAndValidity();
      }, 300);

    this.form.statusChanges.subscribe((state) => {
      // console.log('on form change', this.model);
    });
  }

  onSubmit() {
    if (this.form.valid) {
      this.toast = null;

      // Map form fields to expected model properties
      const mappedModel = {
        ...this.model,
        productSigningUpFor: this.model.productSigningUpFor?.value || this.model.productSigningUpFor,
        annualRevenue: this.model.annualRevenue?.value || this.model.annualRevenue
      };

      // this.signupService.saveUser(this.model);
      this.userInfo = mappedModel;
      let convertedLowecaseModel = this.convertKeysToLowercase(this.userInfo);
      const renamedFields = this.renameTopKeys(convertedLowecaseModel)

      const mappedFields = this.supportService.mapToFields(renamedFields, this.supportService.defaultForm.signupFormData);
      this.isLoading = true;

      this.signupService.registerUser(mappedModel).subscribe({
        next: (response) => {
          this.isLoading = false;
          if (response.success) {
            this.userInfo = { ...this.userInfo, ...response };
            if(response.userId){
              this.store.setUserId(response.userId);
            }
            // this.signupService.saveUser(this.userInfo);
            this.store.setResendLiknTimestamp(Date.now());
            this.messageView = SignupSuccess(this.model.email);
            this.trackingService.trackCustomEvent(NS_Enums.trackingCustomEvents.REGISTER_USER, this.model);
            this.trackingService.trackCustomEvent(NS_Enums.trackingCustomEvents.REGISTER_VERIFICATION_LINK_SENT, this.model);
            this.supportService.submitHubspotForm(mappedFields).subscribe((response:any) => {
              console.log("Hubspot form submission", response)
            })
          } else {
            this.toast = {};
            this.toast.status = 'failure';
            this.toast.subHeading = response.message;
            this.trackingService.trackCustomEvent(NS_Enums.trackingCustomEvents.REGISTER_VERIFICATION_LINK_FAILED, this.model);
          }
        },
      });
    } else {
      this.form.markAllAsTouched();
    }
  }

   /**
   * Renames top-level keys in the object according to mapObject.
   * If a key isn't in the map, it keeps the original key.
   */
  renameTopKeys(obj: any): any {

    const mapObject: Record<string, string> = {
      firstname: "firstname",
      lastname: "lastname",
      email: "email",
      company: "company",
      annualRevenue: "annualRevenue",
      productSigningUpFor: "productSigningUpFor"
    };

    if (obj === null || typeof obj !== "object" || Array.isArray(obj)) {
      return obj;
    }

    const result: any = {};

    Object.entries(obj || {}).forEach(([rawKey, rawVal]) => {
      if (rawKey == null) return;
      
      const lowerKey = rawKey.toLowerCase();
      const newKey = mapObject[lowerKey] || rawKey;

      let newVal = rawVal;

      if (lowerKey === "size" && rawVal != null && typeof rawVal === "object" && 'value' in rawVal) {
        newVal = (rawVal as { value: any }).value;
      }
      if (lowerKey === "country" && rawVal != null && typeof rawVal === "object" && 'name' in rawVal) {
        newVal = (rawVal as { name: any }).name;
      }

      result[newKey] = newVal;
    });

    return result;
  }


  convertKeysToLowercase(obj: any): any {
    if (obj == null) {
      return obj;
    }
    
    if (Array.isArray(obj)) {
      return obj.map(item => this.convertKeysToLowercase(item)); // Recursively process array elements
    }

    if (typeof obj === 'object') {
      const newObj: any = {};
      Object.keys(obj || {}).forEach(key => {
        if (key == null) return;
        
        const lowerKey = key.toLowerCase();
        newObj[lowerKey] = this.convertKeysToLowercase(obj[key]);
      });

      return newObj;
    }
    return obj;
  }

  sendVerificationEmail() {
    if (this.userInfo.userId) {
      this.signupService.sendVerificationEmail(this.userInfo.userId).subscribe({
        next: (response: any) => {
          if (response.success) {
            this.messageView = SignupSuccess(this.model.email);
            this.trackingService.trackCustomEvent(NS_Enums.trackingCustomEvents.REGISTER_RESEND_VERIFICATION_LINK, this.model);
          } else {
            this.messageView = SignupEmailVerificationFailure(
              this.userInfo.email || ''
            );
            this.trackingService.trackCustomEvent(NS_Enums.trackingCustomEvents.REGISTER_RESEND_VERIFICATION_LINK_FAILED, this.model);
          }
        },
      });
    }
  }

  modelChangeHandler(evt: any) {
    // console.log(evt, this.form.valid);
  }

  onMessageViewEvent(evt: any) {
    switch (evt.type) {
      case MessageViewEvent.REFRESH:
        break;
      case MessageViewEvent.RESEND:
        this.sendVerificationEmail();
        break;
    }
  }
}
